import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageCircle, Send, ArrowLeft } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface Conversation {
  id: string;
  otherUser: {
    id: string;
    firstName: string;
    lastName: string;
    username?: string;
    email?: string;
    profilePhotoUrl?: string;
    grade?: number;
  };
  lastMessage?: { content: string; createdAt: string };
  lastMessageAt: string;
  unreadCount: number;
}

interface Message {
  id: string;
  senderId: string;
  content: string;
  createdAt: string;
}

export default function AdminUserMessages() {
  const { toast } = useToast();
  const [selectedConvo, setSelectedConvo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const { data, isLoading } = useQuery<{ conversations: Conversation[]; botUserId: string }>({
    queryKey: ["/api/admin/user-messages"],
  });

  const { data: messages = [] } = useQuery<Message[]>({
    queryKey: ["/api/admin/user-messages", selectedConvo],
    queryFn: async () => {
      const res = await fetch(`/api/admin/user-messages/${selectedConvo}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!selectedConvo,
    refetchInterval: 10000,
  });

  const replyMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(`/api/admin/user-messages/${selectedConvo}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => {
      setReplyText("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/user-messages", selectedConvo] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/user-messages"] });
    },
    onError: () => toast({ title: "Error", description: "Failed to send reply", variant: "destructive" }),
  });

  const handleReply = () => {
    if (!replyText.trim()) return;
    replyMutation.mutate(replyText.trim());
  };

  const conversations = data?.conversations || [];
  const botUserId = data?.botUserId;
  const selectedConversation = conversations.find(c => c.id === selectedConvo);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
        <p className="mt-4 text-muted-foreground">Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageCircle className="w-6 h-6" />
          User Messages
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Messages sent to the EduFocal Bot. Reply as the bot from here.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4" style={{ minHeight: '500px' }}>
        {/* Conversation list */}
        <Card className="md:col-span-1 flex flex-col">
          <CardHeader className="py-3 px-4 flex-shrink-0">
            <CardTitle className="text-base">Conversations ({conversations.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">No messages yet</p>
            ) : (
              <div className="divide-y">
                {conversations.map(convo => (
                  <button
                    key={convo.id}
                    onClick={() => setSelectedConvo(convo.id)}
                    className={`w-full p-3 text-left hover:bg-muted/50 transition-colors ${selectedConvo === convo.id ? 'bg-accent' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage src={convo.otherUser.profilePhotoUrl || ''} />
                        <AvatarFallback className="text-xs">
                          {convo.otherUser.firstName?.[0]}{convo.otherUser.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm truncate">
                            {convo.otherUser.firstName} {convo.otherUser.lastName}
                          </p>
                          {convo.unreadCount > 0 && (
                            <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] flex items-center justify-center text-xs">
                              {convo.unreadCount}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {convo.lastMessage?.content || "No messages"}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(convo.lastMessageAt), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message view */}
        <Card className="md:col-span-2 flex flex-col">
          {!selectedConvo ? (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Select a conversation to view messages</p>
              </div>
            </CardContent>
          ) : (
            <>
              <CardHeader className="py-3 px-4 border-b flex-shrink-0">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSelectedConvo(null)}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={selectedConversation?.otherUser.profilePhotoUrl || ''} />
                    <AvatarFallback className="text-xs">
                      {selectedConversation?.otherUser.firstName?.[0]}{selectedConversation?.otherUser.lastName?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-sm">
                      {selectedConversation?.otherUser.firstName} {selectedConversation?.otherUser.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Grade {selectedConversation?.otherUser.grade}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: '400px' }}>
                {messages.map(msg => {
                  const isBot = msg.senderId === botUserId;
                  return (
                    <div key={msg.id} className={`flex ${isBot ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        isBot ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}>
                        <p className="text-sm break-words whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isBot ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                          {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>

              <div className="border-t p-3 flex gap-2 flex-shrink-0">
                <Input
                  placeholder="Reply as EduFocal Bot..."
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleReply()}
                  disabled={replyMutation.isPending}
                />
                <Button onClick={handleReply} disabled={!replyText.trim() || replyMutation.isPending} size="icon">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
